import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import WorldLabel from './WorldLabel';
import { useWorldStore } from '../state/worldStore';
import AnimatedResident from './AnimatedResident';
import { ROOMS } from '../rooms';
import { listWorldObjects } from '../services/worldObjects';
import CameraBasisProbe from './CameraBasisProbe';
import { useRoomRoster } from '../services/bodyRigs';
import { useCityStore } from '../city/cityStore';
import { useGLTF } from '@react-three/drei';
import SafeMount from './SafeMount';
import { bodyScene, useSceneChat } from './SceneChat';

// InteriorResidents — avatars standing INSIDE a room.
//
// The street places residents by raycasting the city mesh: click the pavement,
// the body lands there. None of that works in a splat. A Gaussian splat has no
// geometry to hit, so there is no floor to snap to and no wall to align
// against — every position is authored by eye, which is why this needs an
// editor and a table rather than a config file.
//
// A body is a BODY by default: no barks, no proximity tiers, nothing that
// ambushes you on entry. What it can be is CAST — and a part with a brain
// (world_actors.data.brain, migration 282) is clickable, because a clerk you
// cannot walk up to is scenery. The rule that survived: the body never decides
// this. The server says which parts answer, and a body only carries a part.


// An unhurried indoor walk is about 1.1 m/s, and 1u ≈ 2.2m.
const WALK = 0.5;
// Fast enough to have finished turning before the walk is noticeable, slow
// enough not to snap. A body that pivots instantly reads as a sprite.
const TURN = 3.4;
// Close enough to have arrived. Below this she stops steering by travel
// direction and settles into the pose's own yaw, otherwise the last centimetre
// of drift spins her on the spot.
const ARRIVED = 0.05;
// TWO thresholds for the walk/idle switch, not one. A single threshold sits
// exactly where the distance is noisiest, so the clip crossfades in and out
// every few frames on arrival — the animation equivalent of the streaming
// gate's mount/unmount thrash. Start walking only once there is a real distance
// to cover; stop only once genuinely there.
const GO = 0.12;
const STOP = 0.04;

// StagedResident — a resident that can be SENT somewhere.
//
// The transform is owned by the frame loop, not by React: `to` changes at most
// a couple of times in a scene, and re-rendering a body every frame to move it
// would be a re-render per frame for a value nothing else reads.
//
// Which is why the initial placement is a layout effect keyed on the AUTHORED
// numbers rather than JSX props. If position lived on the element, any unrelated
// re-render — a stage direction arriving, the editor selecting something — would
// re-apply it and teleport her home mid-walk. Keyed this way she snaps only when
// her placement genuinely changes, which is exactly what you want while nudging
// her in the editor.
function StagedResident({ o, model, to, face, bubble, onSelect, hint, lightScale = 1, groupRef = null, actorKey = null }) {
  const g = useRef();
  // Their face moves while they answer at the counter (ShopDock useChatSpeaking).
  const saying = useSceneChat((st) => actorKey != null && st.speaking === `actor:${actorKey}`);
  // `model` arrives already carrying the server's body, pose and clips — the
  // parent layers them on so this component never sees a stale roster entry.
  // Whoever this body is looking at, as a position. A ref rather than a dep: the
  // frame loop owns the transform (see below), and re-rendering a body because
  // somebody else started a sentence is the thing that would make an ensemble
  // expensive.
  const faceRef = useRef(face);
  faceRef.current = face;
  // A row saved before `pos` was written is a placement bug, not a crash — put
  // the body at the room's origin where it is obviously wrong and findable.
  const at = o.pos || [0, 0, 0];
  // A POSED body is FURNITURE-BOUND — someone in a chair does not walk to a
  // mark, and does not swivel the whole body to face whoever is talking. So a
  // pose overrides any ensemble target: it stays where it was placed, at the
  // yaw it was placed at. Its head still turns and still talks (AnimatedResident
  // owns that), which is the part of "listening" you actually read from across
  // a room. Authored residents carry `pose`; placed rows carry `data.pose`.
  const poseKey = o.pose || o.data?.pose || null;
  const target = (poseKey || !to) ? { pos: at, rotY: o.rotY || 0 } : to;
  // Which clip she is playing. STATE, unlike the transform, because it crosses
  // into React — twice per trip rather than once per frame, so the re-render is
  // affordable and the alternative (reaching into the mixer from here) would
  // duplicate what AnimatedResident already does correctly.
  const [moving, setMoving] = useState(false);
  const movingRef = useRef(false);

  useLayoutEffect(() => {
    const gr = g.current;
    if (!gr) return;
    gr.position.set(at[0], at[1], at[2]);
    gr.rotation.y = o.rotY || 0;
  }, [at[0], at[1], at[2], o.rotY]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((_, dt) => {
    const gr = g.current;
    if (!gr) return;
    const [tx, ty, tz] = target.pos;
    const dx = tx - gr.position.x;
    const dz = tz - gr.position.z;
    const dist = Math.hypot(dx, dz);

    // FACE WHERE YOU ARE GOING while there is anywhere to go. Without this she
    // crabs sideways across the room still facing the reception desk, which is
    // the single clearest tell that a position is being interpolated rather than
    // a person walking. Only on arrival does the mark's own yaw take over.
    //
    // …and once standing still, whoever is being TALKED TO outranks the placed
    // yaw. For a stationary ensemble that turn is the entire performance: nobody
    // walks, nobody gestures yet, so the difference between three bodies in a
    // room and a conversation is which way their heads are pointed. Falling back
    // to `target.rotY` when nobody is speaking is what returns them to their
    // placed pose afterwards, without a second mechanism to put them back.
    const look = faceRef.current;
    const want = poseKey
      ? (target.rotY || 0)                        // pinned by the furniture
      : dist > ARRIVED
        ? Math.atan2(dx, dz)
        : (look ? Math.atan2(look[0] - gr.position.x, look[2] - gr.position.z) : (target.rotY || 0));
    const d = Math.atan2(Math.sin(want - gr.rotation.y), Math.cos(want - gr.rotation.y));
    gr.rotation.y += THREE.MathUtils.clamp(d, -TURN * dt, TURN * dt);

    if (dist > 1e-4) {
      const k = Math.min(1, (WALK * dt) / dist);   // never overshoot the target
      gr.position.x += dx * k;
      gr.position.z += dz * k;
    }
    gr.position.y += (ty - gr.position.y) * Math.min(1, dt * 4);

    const go = movingRef.current ? dist > STOP : dist > GO;
    if (go !== movingRef.current) { movingRef.current = go; setMoving(go); }
  });

  // position/rotationY stay at zero on the resident itself — the outer group is
  // the transform now, and passing both would apply it twice.
  return (
    <group ref={(el) => { g.current = el; groupRef?.(el); }}>
      <AnimatedResident
        meshUrl={model.rigged}
        speaking={saying}
        rigPose={model.pose || null}
        clipMap={model.clips || null}
        clipUrls={[model.walk, model.idle].filter(Boolean)}
        play={moving ? 'walk' : 'idle'}
        pose={poseKey}
        position={[0, 0, 0]}
        rotationY={0}
        height={o.size}
        // INDOORS: the body carries its own golden-hour rig instead of standing
        // in whatever the sky is doing (AnimatedResident's indoor block). A
        // resident in here is BY DEFINITION inside a room, so this is not a
        // condition — it is what this component is.
        indoor
        lightScale={lightScale}
        // Clickable only when the part behind this body has a BRAIN. An ensemble
        // member is scenery that talks near you; a clerk is somebody you walk up
        // to, and the cursor should be the one telling you which is which.
        //
        // Note what is NOT passed: `attentionRadius`. AnimatedResident's own
        // proximity turn measures against the position PROP, and in here that
        // prop is [0,0,0] because the outer group owns the transform — it would
        // measure the distance to the room's origin and turn everybody at once.
        onSelect={onSelect}
      />
      {/* A clerk has to READ as a clerk. The cursor already changes on hover, but
          hover is not discoverable in a room you are looking around inside — and
          on a phone it does not exist at all. One small plate says there is
          somebody here to talk to; it goes away while you are talking to them. */}
      {hint && !bubble && (
        <WorldLabel position={[0, o.size + 0.22, 0]} center distanceFactor={7} style={{ pointerEvents: 'none' }}>
          <div style={{
            background: 'rgba(8,12,9,0.8)', color: '#9dff7d', border: '1px solid rgba(157,255,125,0.4)',
            borderRadius: 999, padding: '3px 10px', font: '600 11px system-ui', whiteSpace: 'nowrap',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
          }}>
            {hint}
          </div>
        </WorldLabel>
      )}
      {/* The line, over the head that is saying it. Inside the group on purpose:
          a bubble anchored to the placement would stay behind if the body were
          ever staged somewhere mid-sentence. */}
      {bubble && (
        <WorldLabel position={[0, o.size + 0.28, 0]} center distanceFactor={5} style={{ pointerEvents: 'none' }}>
          <div style={{
            // WIDTH, NOT MAX-WIDTH. A drei <Html> is anchored to a POINT and its
            // wrapper has no width of its own, so a child with only `maxWidth`
            // shrink-wraps to its longest unbreakable word — which is how a
            // sentence became a one-word-per-line column taller than the room.
            // The same collapse as the portfolio grid: state the width.
            width: 260, maxWidth: 'none', boxSizing: 'border-box',
            background: 'rgba(14,10,8,0.86)', color: '#fff2df',
            border: '1px solid rgba(255,178,94,0.32)', borderRadius: 12,
            padding: '7px 11px 8px', font: '13px/1.35 system-ui, sans-serif',
            boxShadow: '0 6px 22px rgba(0,0,0,0.45)', textAlign: 'left',
            overflowWrap: 'break-word',
            backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          }}>
            {bubble.name && (
              <div style={{ font: '600 9.5px system-ui', letterSpacing: 1.2, textTransform: 'uppercase', color: '#ffb25e', marginBottom: 3 }}>
                {bubble.name}
              </div>
            )}
            {bubble.text}
          </div>
        </WorldLabel>
      )}
    </group>
  );
}

export default function InteriorResidents({ roomId }) {
  const [rosterRaw, setRoster] = useState([]);
  /* ⚠ roster.json is the METADATA register, not the current model. Its GLB
     paths are a generation behind for 8 of the 14 bodies — the live mesh,
     its pose and its gesture map come off the server, wholesale. */
  // …plus the bodies built since on the server (useRoomRoster), so anyone the panel offers can be drawn.
  const roster = useRoomRoster(rosterRaw);
  const staging = useWorldStore((s) => s.staging);
  const objects = useWorldStore((s) => s.worldObjects);
  const banter = useWorldStore((s) => s.banter);
  const ensemble = useWorldStore((s) => s.ensemble);
  const openShop = useWorldStore((s) => s.openShop);
  const shopOpen = useWorldStore((s) => !!s.shop);
  const shopNow = useWorldStore((s) => s.shop);
  const camera = useThree((s) => s.camera);
  const anchors = useRef(new Map());   // object_id → the group a body stands in (a counter scene follows it)
  const setObjects = useWorldStore((s) => s.loadWorldObjectSlice);
  // The room's OWNER is never a standing body here (2026-10-31, Khari: "there is
  // a duplicate resident of him there, but we should replace that with a live
  // presence when he is actually there"): RoomHost draws them, from the city's
  // own timeline, only while they are in. Their placed row stays (the panel
  // still lists it); it is just not drawn.
  const ownerId = useCityStore((s) => s.snap?.rooms?.[roomId]?.host ?? null);
  const sel = useWorldStore((s) => s.worldObjectSel);
  const editing = useWorldStore((s) => s.adminTool) === 'iresident';

  useEffect(() => {
    fetch('/models/avatars/roster.json', { cache: 'no-store' })
      .then((r) => r.json()).then((d) => setRoster(d.npcs || [])).catch(() => setRoster([]));
  }, []);

  // Saved rows ARE the arrangement — but PER BODY, not per room.
  //
  // This used to be all-or-nothing: any saved row at all and the room's authored
  // residents were dropped entirely. That is fine while a room has one occupant
  // and quietly catastrophic the moment it has an ensemble — placing the salon's
  // barber would save one row, and Sassy, who exists only in rooms.js, would
  // disappear from her own shop along with the scene that sends her to the chair.
  // Nobody deleted her; she was never a row to delete.
  //
  // So: an authored resident is superseded by a SAVED ROW FOR THE SAME SLUG (the
  // panel writes one the first time you nudge her, and two of her would be
  // worse), and otherwise stands. Adding somebody to a room is now purely
  // additive, which is what arranging a room should be.
  useEffect(() => {
    if (!roomId) return;
    let dead = false;
    listWorldObjects(roomId, 'resident').then((rows) => {
      if (dead) return;
      const saved = new Set((rows || []).map((r) => r.ref));
      const authored = (ROOMS[roomId]?.residents || [])
        .filter((e) => !saved.has(e.slug))
        .map((e, i) => ({
          object_id: `authored:${i}`, zone_id: roomId, kind: 'resident',
          ref: e.slug, slot: i, pos: e.pos, rotY: e.rotY || 0, size: e.height || 1.7,
          // `actor` on an authored resident reads the same as `data.actor` on a
          // saved row, so bubbles and facing work for a body that exists only in
          // rooms.js. It does NOT make the room talk: the API casts from SAVED
          // ROWS (one server, one answer, so two players in a room never watch
          // different conversations) and cannot see this file. An ensemble has to
          // be placed and saved to have a scene — this only means an authored
          // body already in the cast list is drawn correctly when it does.
          data: e.actor ? { actor: e.actor } : null,
        }));
      setObjects(roomId, 'resident', [...(rows || []), ...authored]);
    });
    return () => { dead = true; };
  }, [roomId, setObjects]);

  const mine = objects.filter((o) => o.zone_id === roomId && o.kind === 'resident'
    && !(ownerId != null && Number(o.data?.avatarId) === Number(ownerId)));

  // WHO IS PLAYING WHAT, in this room, right now. `data.actor` is the one link
  // between a body and a part (migration 281) — everything else about the
  // ensemble is server-side, and this map is the whole of what the renderer
  // needs to know: where each part is standing, so a speaker can turn to one.
  const spots = {};
  for (const o of mine) { if (o.data?.actor) spots[o.data.actor] = o.pos; }
  // The cast comes from the ensemble read (one server, one answer about who is
  // playing what) — the same payload the banter uses, so a room with no scene
  // still knows its people.
  const cast = (ensemble?.zone === roomId ? ensemble.cast : null) || [];
  // HOW MUCH LIGHT THE BODY HAS TO BRING ITSELF — "use a light from the room, or
  // something brighter… if the room's lighting doesn't work" (Khari, 2026-08-04).
  //
  // A MESH venue lights its own interior (MeshInterior's hemisphere + ambient +
  // ceiling practicals, scaled by ROOMS.<id>.light), and those lamps reach the
  // people standing in it. There the body only needs a lift, or a stylist tuned
  // by eye gets blown out by a second rig nobody asked for.
  //
  // A SPLAT room lights nothing. A gaussian splat carries its own baked
  // exposure and scene lights pass straight through it, so a body in there has
  // only the sky — which after dark is the complaint. Full rig.
  const roomLight = (ROOMS[roomId]?.light ?? 1) * (ROOMS[roomId]?.model ? 0.45 : 1);
  const speakingAt = banter?.actor ? spots[banter.actor] : null;
  const spokenToAt = banter?.to ? spots[banter.to] : null;

  // One line per room, so "is it even reaching this component" is answerable
  // without reading the store.
  useEffect(() => {
    console.log(`[resident] room "${roomId}": ${mine.length} placed · roster ${roster.length} model(s)`
      + (mine.length ? ` · ${mine.map((o) => `${o.ref}@${(o.pos||[]).map((v) => Number(v).toFixed(1)).join(",")} h${o.size}`).join(" | ")}` : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, mine.length, roster.length]);

  // SAY WHY NOBODY IS THERE.
  //
  // Three separate conditions here return null and none of them made a sound: an
  // empty roster, a `ref` with no roster entry, and a roster entry that has no
  // rigged mesh. All three look identical from inside the room — you place a
  // resident, the row saves, and the floor stays empty — so the tool reads as
  // broken when in fact it is reporting a missing model by drawing nothing.
  if (!roster.length) {
    if (mine.length) console.warn(`[resident] ${mine.length} placed in "${roomId}" but the roster is empty — /models/avatars/roster.json failed to load`);
    return null;
  }

  return (
    <>
      {editing && <CameraBasisProbe />}
      {mine.map((o) => {
        const model = roster.find((n) => n.slug === o.ref);
        if (!model) {
          console.warn(`[resident] "${o.ref}" in "${roomId}" is not in roster.json — nothing to draw. Known: ${roster.map((n) => n.slug).join(', ')}`);
          return null;
        }
        if (!model.rigged) {
          console.warn(`[resident] "${o.ref}" has no rigged mesh in roster.json — it cannot be placed in a room yet`);
          return null;
        }
        // WHERE THE SCENE WANTS HER, if anywhere. The store holds a mark NAME;
        // the room holds what that name means. Unresolvable names are said out
        // loud — a character who quietly fails to move looks like a broken walk
        // rather than a typo in a cue.
        //
        // BY JOB AS WELL AS BY BODY (the café, 2026-10-30): a barista is whoever
        // is cast, so the order logic sends `actor:<role key>` to a mark, and a
        // room's '*' marks belong to whoever stands in the job. A body's own
        // marks and a cue sent to the body still come first.
        const actor = o.data?.actor || null;
        const wanted = staging[o.ref] ?? (actor ? staging[`actor:${actor}`] : undefined);
        const marks = ROOMS[roomId]?.stage?.[o.ref] || ROOMS[roomId]?.stage?.['*'] || null;
        const to = wanted ? (marks?.[wanted] || null) : null;
        if (wanted && !to) {
          console.warn(`[stage] "${o.ref}" was sent to "${wanted}" but ROOMS.${roomId}.stage.${o.ref} has no such mark`
            + ` — known: ${marks ? Object.keys(marks).join(', ') || '(none)' : '(no stage block)'}`);
        }
        // The two halves of a turn: the speaker looks at whoever they addressed,
        // and that person looks back. A line with no `to` is said to the room —
        // nobody turns, which is its own kind of staging.
        const speaking = !!actor && banter?.actor === actor;
        const addressed = !!actor && banter?.to === actor;
        // At their counter with you, as a scene: they look at you (where you stood when you asked).
        const atCounter = !!actor && shopNow?.actorKey === actor && Array.isArray(shopNow?.scene?.toward) ? shopNow.scene.toward : null;
        const face = atCounter || (speaking ? (spokenToAt || null) : (addressed ? (speakingAt || null) : null));

        // SOMEBODY WITH A JOB THAT HAS TOOLS is somebody you can talk to, and
        // the cast says so server-side — the world never decides that for
        // itself, so a body cannot start answering because a client guessed it
        // should.
        //
        // Tested for TRUTHINESS rather than for 'record_shop'. Under roles
        // (migration 284) the server reports 'role' for any job carrying a tool
        // grant, so pinning this to the shop's name would have made every future
        // counter unclickable — and would have muted the clerk the moment 284
        // ran, since his grant is what makes him answer now. Legacy pre-284 rows
        // still report 'record_shop' and match the same test.
        const part = actor ? cast.find((c) => c.actorKey === actor) : null;
        const onSelect = part?.brain && !editing
          ? () => {
            // AS A SCENE where the room says so (rooms.js `chatScene`, components/SceneChat):
            // they turn to you (`toward`, in the space they stand in) and the camera comes to them.
            const anchor = ROOMS[roomId]?.chatScene ? anchors.current.get(o.object_id) : null;
            const me = anchor?.parent ? anchor.parent.worldToLocal(camera.getWorldPosition(new THREE.Vector3())) : null;
            const scene = anchor ? { ...bodyScene(anchor, { height: o.size, sub: part.role }), toward: me ? [me.x, me.y, me.z] : null } : null;
            openShop({ actorKey: part.actorKey, name: part.name, role: part.role, zone: roomId, scene });
          }
          : null;
        // Named only while there is nobody at his counter — once the dock is
        // open the plate is repeating what the dock's own header already says.
        const hintFor = onSelect && !shopOpen ? (part.name || part.role || 'talk') : null;

        return (
          <React.Fragment key={o.object_id}>
            {/* SUSPENSE PER BODY — the boundary this component never had.
                A resident's GLB suspends while it downloads, and without a
                boundary of its own that suspension travels UP to the room's,
                blanking everything beside it. In a mesh venue nobody noticed:
                the building is a GLB too, so it was already waiting. In a SPLAT
                room it is fatal — placing one body tore down the splat mid-load
                (SplatWorld's cleanup removes the mesh), and the shop fell back
                to its procedural stand-in with the real room never arriving.
                Bouncers already wraps each body this way; this is the same fix
                at the place bodies are actually placed. */}
            {/* ⚠ SafeMount, NOT A BARE SUSPENSE — and this became necessary the moment these
                bodies stopped being local files. A bundled GLB is always there; an
                API-served one can 404, stall or fail CORS, and the dev server answers
                an unknown path with index.html, so GLTFLoader gets HTML and THROWS.
                Unwrapped that throw goes past Suspense and takes the Canvas — and the
                WebGL context — with it. Same net and retry the street and Peers use. */}
            <SafeMount
              label={`resident ${o.ref}`}
              retryMs={30000}
              onRetry={() => useGLTF.clear([model.rigged, model.walk, model.idle].filter(Boolean))}
            >
              <Suspense fallback={null}>
                <StagedResident o={o} model={model} to={to} face={face} bubble={speaking ? banter : null} onSelect={onSelect} hint={hintFor} lightScale={roomLight}
                  actorKey={actor} groupRef={(el) => { if (el) anchors.current.set(o.object_id, el); else anchors.current.delete(o.object_id); }} />
              </Suspense>
            </SafeMount>
            {editing && sel === o.object_id && (
              <WorldLabel position={[o.pos[0], o.pos[1] + o.size + 0.25, o.pos[2]]} center distanceFactor={6} style={{ pointerEvents: 'none' }}>
                <div style={{ background: '#ffb25e', color: '#1c1206', font: '600 11px system-ui', padding: '3px 7px', borderRadius: 6, whiteSpace: 'nowrap' }}>
                  {o.ref} ◄ placing
                </div>
              </WorldLabel>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}
